import { useState, useEffect, useCallback } from 'react';
import { ChevronDown, CarFront } from 'lucide-react';
import { ExportCSVButton, ExportPDFButton } from '../components/ExportButtons';
import { getTopMembersReport, getTopMemberBookings, listAllGolfCourses, listSettings } from '../api';
import { fmtReportDate, exportCSV, fmtCSVFilename } from '../utils/reportExport';
import PrintBanner from '../components/PrintBanner/PrintBanner';
import ReportFilterBar from '../components/ReportFilterBar';
import styles from './TopMembersReportPage.module.css';
import HolePill from '../components/HolePill';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmtDate(iso) {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

function fmtTime(t) {
  if (!t) return '—';
  const match = t.match(/(\d{1,2}):(\d{2})/);
  if (!match) return t;
  let h = parseInt(match[1], 10);
  const m = match[2];
  const ap = h < 12 ? 'AM' : 'PM';
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;
  return `${h}:${m} ${ap}`;
}

// ---------------------------------------------------------------------------
// Member row (expandable)
// ---------------------------------------------------------------------------

function MemberRow({ member, defaultOpen, dateFrom, dateTo, courseId }) {
  const [open, setOpen]           = useState(defaultOpen);
  const [bookings, setBookings]   = useState(member.bookings || []);
  const [nextCursor, setNextCursor] = useState(member.next_cursor || null);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    setOpen(defaultOpen);
  }, [defaultOpen]);

  // Reset local bookings when member data changes (filter change)
  useEffect(() => {
    setBookings(member.bookings || []);
    setNextCursor(member.next_cursor || null);
  }, [member.bookings, member.next_cursor]);

  const handleLoadMoreBookings = () => {
    if (!nextCursor) return;
    setLoadingMore(true);
    getTopMemberBookings(member.member_id, {
      dateFrom, dateTo,
      courseId: courseId || undefined,
      cursor: nextCursor,
    })
      .then(d => {
        setBookings(prev => [...prev, ...(d.bookings || [])]);
        setNextCursor(d.next_cursor || null);
        setLoadingMore(false);
      })
      .catch(() => setLoadingMore(false));
  };

  return (
    <>
      <tr
        className={`${styles.memberRow} ${styles.memberRowClickable}`}
        onClick={() => setOpen(v => !v)}
        role="button"
        aria-expanded={open}
      >
        <td className={styles.memberTd}>
          <div className={styles.memberInner}>
            <div className={styles.memberInfo}>
              <div className={styles.memberName}>{member.member_name}</div>
              <div className={styles.memberNum}>{member.member_number}</div>
            </div>
          </div>
        </td>
        <td className={styles.statCell}>
          <div className={styles.countVal}>{member.paid_booking_count}</div>
          <div className={styles.countLabel}>
            paid booking{member.paid_booking_count !== 1 ? 's' : ''}
          </div>
        </td>
        <td className={styles.statCell}>
          <div className={styles.statVal}>{member.holes_played ?? '—'}</div>
          <div className={styles.statLabel}>holes played</div>
        </td>
        <td className={styles.statCell}>
          <div className={styles.statVal}>{member.carts_used ?? '—'}</div>
          <div className={styles.statLabel}>carts used</div>
        </td>
        <td className={styles.chevronCell}>
          <ChevronDown
            size={16}
            strokeWidth={1.5}
            className={`${styles.chevron} ${open ? styles.chevronOpen : ''}`}
          />
        </td>
      </tr>
      {open && (
        <tr className={styles.memberRow}>
          <td colSpan={5} className={styles.memberTd}>
            <div className={styles.subreport}>
              <table className={styles.subTable}>
                <thead>
                  <tr>
                    <th>Booking #</th>
                    <th>Date</th>
                    <th>Time</th>
                    <th>Golf course</th>
                    <th>Hole</th>
                    <th>Needs cart?</th>
                    <th>Holes to play</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((bk, i) => (
                    <tr key={i}>
                      <td className={styles.mono}>{bk.booking_number}</td>
                      <td>{fmtDate(bk.date)}</td>
                      <td className={styles.muted}>{fmtTime(bk.time)}</td>
                      <td className={styles.muted}>{bk.golf_course_name || '—'}</td>
                      <td>
                      <HolePill hole={bk.hole} />
                    </td>
                      <td>
                        {bk.needs_cart ? (
                          <span className={styles.cartYes}>
                            <CarFront size={13} strokeWidth={1.5} />
                            Yes
                          </span>
                        ) : (
                          <span className={styles.cartNo}>No</span>
                        )}
                      </td>
                      <td className={styles.muted}>{bk.holes_to_play || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {nextCursor && (
                <div className={styles.loadMoreBookings}>
                  <button
                    className={styles.btnLoadMoreSub}
                    onClick={handleLoadMoreBookings}
                    disabled={loadingMore}
                  >
                    {loadingMore ? 'Loading...' : 'Load more bookings'}
                  </button>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

function currentMonthStart() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

function currentMonthEnd() {
  const d = new Date();
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return `${last.getFullYear()}-${String(last.getMonth() + 1).padStart(2, '0')}-${String(last.getDate()).padStart(2, '0')}`;
}

export default function TopMembersReportPage() {
  const [dateFrom, setDateFrom]     = useState(currentMonthStart);
  const [dateTo, setDateTo]         = useState(currentMonthEnd);
  const [courseId, setCourseId]     = useState('');
  const [courses, setCourses]       = useState([]);

  const [members, setMembers]       = useState([]);
  const [total, setTotal]           = useState(null);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading]       = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError]           = useState(null);
  const [expandAll, setExpandAll]   = useState(false);
  const [applied, setApplied]       = useState(null);
  const [companyInfo, setCompanyInfo] = useState({ name: '', address: '', website: '', logo: '' });

  useEffect(() => {
    listAllGolfCourses()
      .then(r => setCourses(r.items || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    listSettings()
      .then(data => {
        const map = {};
        (data.settings || data.items || []).forEach(s => { map[s.key] = s.value; });
        setCompanyInfo({ name: map.company_name || '', address: map.company_address || '', website: map.company_website || '', logo: map.company_logo || '' });
      })
      .catch(() => {});
  }, []);

  const fetchMembers = useCallback((params, append = false) => {
    if (append) setLoadingMore(true);
    else { setLoading(true); setMembers([]); setNextCursor(null); setTotal(null); }
    setError(null);

    getTopMembersReport(params)
      .then(d => {
        setMembers(prev => append ? [...prev, ...(d.members || [])] : (d.members || []));
        setNextCursor(d.next_cursor || null);
        setTotal(d.total ?? null);
        if (append) setLoadingMore(false);
        else setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        if (append) setLoadingMore(false);
        else setLoading(false);
      });
  }, []);

  const handleApply = () => {
    const params = {
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      courseId: courseId || undefined,
    };
    setApplied(params);
    setExpandAll(false);
    fetchMembers(params);
  };

  const handleClear = () => {
    setDateFrom(currentMonthStart());
    setDateTo(currentMonthEnd());
    setCourseId('');
    setApplied(null);
    setMembers([]);
    setTotal(null);
    setNextCursor(null);
    setExpandAll(false);
    setError(null);
  };

  const handleLoadMore = () => {
    if (!nextCursor || !applied) return;
    fetchMembers({ ...applied, cursor: nextCursor }, true);
  };

  const handleExportCSV = () => {
    if (!members.length) return;
    const headers = ['Member Name', 'Member #', 'Paid Bookings', 'Holes Played', 'Carts Used'];
    const rows = members.map(m => [
      m.member_name || '',
      m.member_number || '',
      m.paid_booking_count ?? '',
      m.holes_played ?? '',
      m.carts_used ?? '',
    ]);
    exportCSV(headers, rows, fmtCSVFilename('top_members', applied?.dateFrom, applied?.dateTo));
  };

  return (
    <div className={styles.page}>
      {/* Filter bar — hidden when printing */}
      <div className="no-print">
        <ReportFilterBar
          title="Top members"
          dateFrom={dateFrom}
          dateTo={dateTo}
          onDateFromChange={setDateFrom}
          onDateToChange={setDateTo}
          dateFromLabel="Start date"
          dateToLabel="End date"
          courseId={courseId}
          courses={courses}
          onCourseChange={setCourseId}
          onApply={handleApply}
          onClear={handleClear}
        >
          <span className={styles.sep} />
          <button
            className={styles.btnSecondary}
            onClick={() => setExpandAll(v => !v)}
          >
            <ChevronDown size={12} strokeWidth={1.5} />
            {expandAll ? 'Collapse all' : 'Expand all'}
          </button>
          {members.length > 0 && (
            <>
              <span className={styles.sep} />
              <ExportCSVButton onClick={handleExportCSV} />
              <ExportPDFButton onClick={() => window.print()} />
            </>
          )}
        </ReportFilterBar>
      </div>{/* end no-print */}

      {/* Body */}
      <div className={styles.body}>
        <PrintBanner
          companyInfo={companyInfo}
          reportTitle="Top Members"
          dateRange={applied?.dateFrom && applied?.dateTo ? `${fmtReportDate(applied.dateFrom)} – ${fmtReportDate(applied.dateTo)}` : undefined}
          course={applied?.courseId ? (courses.find(c => String(c.id) === String(applied.courseId))?.name || 'All courses') : 'All courses'}
          generatedOn={new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
        />
        {error && <div className={styles.errorState}>Failed to load data. Please try again.</div>}
        {loading && <div className={styles.emptyState}>Loading...</div>}
        {!loading && !error && members.length === 0 && (
          <div className={styles.emptyState}>
            {applied ? 'No members match the selected filters.' : 'Set filters and click Apply to load top members.'}
          </div>
        )}
        {!loading && members.length > 0 && (
          <>
            <table className={styles.table}>
              <tbody>
                {members.map((m, i) => (
                  <MemberRow
                    key={m.member_id ?? i}
                    member={m}
                    defaultOpen={expandAll}
                    dateFrom={applied?.dateFrom}
                    dateTo={applied?.dateTo}
                    courseId={applied?.courseId}
                  />
                ))}
              </tbody>
            </table>

            {nextCursor && (
              <div className={styles.loadMore}>
                <button
                  className={styles.btnLoadMore}
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                >
                  {loadingMore ? 'Loading...' : 'Load more members'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
